import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Cpu, Edit, AlertCircle, Sparkles, Mic, ListChecks } from 'lucide-react';
import { apiService, LLMProvider, LLMModel, LLMParameterDefinition, LLMParameterValue } from '../../services/api';
import { toast } from 'sonner';

/**
 * Gemeinsame LLM-Auswahl-Form (Provider + Modell + optionale Modell-Parameter)
 * der Background-Job-Konfigurationen; nur Endpoint/Beschriftung unterscheiden
 * sich. `null`-Felder bedeuten: Funktion ist noch unkonfiguriert (kein Default,
 * kein Fallback — der Job schlägt bis zur Konfiguration mit Fehler fehl).
 */
interface LlmChoice {
  provider: string | null;
  model: string | null;
  /** Konfigurierte Modell-Parameter (CA-582) oder `null` = Code-Defaults der Funktion. */
  llmParameters?: Record<string, LLMParameterValue> | null;
}

/** Formular-Zustand des Edit-Dialogs (immer Strings, leer = nicht gewählt). */
interface LlmChoiceForm {
  provider: string;
  model: string;
  /** Nur explizit gesetzte Parameter — fehlende Keys = Modell-/Code-Default. */
  llmParameters: Record<string, LLMParameterValue>;
}

/**
 * Deklarative Beschreibung einer Background-Job-LLM-Zeile. Kapselt Beschriftung,
 * Endpoint-Zugriff (load/save) und Toast-Text, damit Tabelle + Edit-Dialog für alle
 * Zeilen datengetrieben statt per Copy-Paste gerendert werden.
 */
interface BackgroundJobLlmDefinition {
  id: string;
  label: string;
  icon: typeof Cpu;
  iconClassName: string;
  description: string;
  dialogTitle: string;
  dialogDescription: string;
  savedMessage: string;
  load: () => Promise<LlmChoice>;
  save: (data: Partial<LlmChoiceForm>) => Promise<LlmChoice>;
  /**
   * Schränkt die wählbaren Provider zusätzlich ein (z.B. Transkription: nur
   * Whisper-fähige Driver). Inaktive Provider bleiben wählbar (mit Badge
   * markiert), damit Konfigurationen vorbereitet werden können.
   */
  providerFilter?: (provider: LLMProvider) => boolean;
  /**
   * `true` = Modell wird als Freitext eingegeben (Modell-Liste `llm_models`
   * liefert nur Autocomplete-Vorschläge). Nötig für die Transkription, weil
   * Whisper-Deployment-Namen (v.a. Azure OpenAI) von den katalogisierten
   * Modell-Keys abweichen. Default (nicht gesetzt): Katalog-Dropdown.
   */
  modelFreetext?: boolean;
  /** Optionaler Hinweistext unter dem Modellfeld (nur bei `modelFreetext`). */
  modelHint?: string;
  /**
   * `true` = im Edit-Dialog sind Modell-Parameter (temperature, max_tokens,
   * reasoning_effort, …) konfigurierbar (CA-582). Angebotene Felder kommen aus
   * dem aufgelösten Parameter-Katalog des gewählten Modells. Nicht für die
   * Transkription (Whisper kennt diese Parameter nicht).
   */
  supportsParameters?: boolean;
}

/** Whisper-fähige Driver für die Diktat-Transkription. */
const TRANSCRIPTION_CAPABLE_DRIVERS: ReadonlySet<string> = new Set(['openai', 'azure-openai']);

const JOB_DEFINITIONS: BackgroundJobLlmDefinition[] = [
  {
    id: 'hook',
    label: 'Hook-Generierung',
    icon: Cpu,
    iconClassName: 'text-amber-600',
    description:
      'Generiert Email- und SMS-Hook-Inhalte per LLM, wenn der Inhaltsmodus „Prompt" gewählt ist.',
    dialogTitle: 'Hook-Generierung: LLM bearbeiten',
    dialogDescription:
      'Provider und Modell für die Generierung von Email-/SMS-Hook-Inhalten (Inhaltsmodus „Prompt")',
    savedMessage: 'Einstellung für Hook-Generierung gespeichert',
    load: () => apiService.getHookLlmConfig(),
    save: (data) => apiService.updateHookLlmConfig(data),
    supportsParameters: true,
  },
  {
    id: 'internal-ai',
    label: 'KI-Assistent & Prompt-Werkzeuge',
    icon: Sparkles,
    iconClassName: 'text-violet-600',
    description:
      'LLM für die kundenseitigen KI-Werkzeuge: Einrichtungsassistent, Prompt-Check/-Optimierung und Szenario-Replay.',
    dialogTitle: 'KI-Assistent & Prompt-Werkzeuge: LLM bearbeiten',
    dialogDescription:
      'Provider und Modell für die kundenseitigen KI-Werkzeuge (Einrichtungsassistent, Prompt-Check/-Optimierung, Szenario-Replay)',
    savedMessage: 'Einstellung für KI-Assistent & Prompt-Werkzeuge gespeichert',
    load: () => apiService.getInternalAiLlmConfig(),
    save: (data) => apiService.updateInternalAiLlmConfig(data),
    supportsParameters: true,
  },
  {
    id: 'transcription',
    label: 'Audio-Transkription (Diktat)',
    icon: Mic,
    iconClassName: 'text-sky-600',
    description:
      'Whisper-Transkription der Spracheingabe im KI-Einrichtungsassistenten. Modell: OpenAI-Modellname (whisper-1) bzw. Azure-Whisper-Deployment-Name. API-Key kommt aus der Provider-Konfiguration des gewählten Providers.',
    dialogTitle: 'Audio-Transkription: LLM bearbeiten',
    dialogDescription:
      'Provider und Whisper-Modell/-Deployment für die Diktat-Transkription (z.B. azure-openai-eu für EU-Datenresidenz)',
    savedMessage: 'Einstellung für Audio-Transkription gespeichert',
    load: () => apiService.getTranscriptionLlmConfig(),
    save: (data) => apiService.updateTranscriptionLlmConfig(data),
    providerFilter: (provider) => TRANSCRIPTION_CAPABLE_DRIVERS.has(provider.driverType ?? ''),
    modelFreetext: true,
    modelHint:
      'OpenAI-Treiber: Modellname (z.B. whisper-1). Azure-OpenAI-Treiber: Name des Whisper-Deployments in der Azure-Ressource des Providers.',
  },
  {
    id: 'hook-evaluation',
    label: 'Hook-Evaluation (Bedingungsprüfung)',
    icon: ListChecks,
    iconClassName: 'text-emerald-600',
    description:
      'Wertet nach dem Gespräch per LLM aus, ob die Bedingung eines Conditional-Hooks erfüllt ist (CA-450).',
    dialogTitle: 'Hook-Evaluation: LLM bearbeiten',
    dialogDescription:
      'Provider und Modell für die Post-Call-Auswertung von Conditional-Hook-Bedingungen',
    savedMessage: 'Einstellung für Hook-Evaluation gespeichert',
    load: () => apiService.getHookEvaluationLlmConfig(),
    save: (data) => apiService.updateHookEvaluationLlmConfig(data),
    supportsParameters: true,
  },
];

/**
 * Admin-Seite "Background Job LLMs" — konfiguriert pro Hintergrund-Job-Funktion,
 * welcher LLM-Provider/welches Modell für die serverseitige Content-Generierung genutzt
 * wird. Zeilen sind datengetrieben (siehe JOB_DEFINITIONS): Hook-Generierung,
 * kundenseitige KI-Werkzeuge (Einrichtungsassistent, Prompt-Check/-Optimierung,
 * Szenario-Replay), Audio-Transkription (Diktat) und Hook-Evaluation (CA-450).
 * Es gibt keine fest verdrahteten Defaults: unkonfigurierte Funktionen werden mit
 * „Nicht konfiguriert"-Badge angezeigt und schlagen zur Laufzeit mit klarer
 * Fehlermeldung fehl, bis ein Provider gewählt wurde. Alle Provider sind
 * admin-verwaltet (die früheren Built-ins sind deaktiviert).
 *
 * Listen-/Tabellen-Stil analog zur Kunden-Übersicht (eine Zeile pro Funktion,
 * Aktionen-Dropdown rechts).
 */
export function BackgroundJobLlmSettingsPage() {
  const [configs, setConfigs] = useState<Record<string, LlmChoice | null>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [models, setModels] = useState<LLMModel[]>([]);
  // Bewusst leer vorbelegt — es gibt keine Default-Provider/-Modelle mehr.
  const [formData, setFormData] = useState<LlmChoiceForm>({
    provider: '',
    model: '',
    llmParameters: {},
  });
  /**
   * Aufgelöste Parameter-Definitionen des im Dialog gewählten Modells
   * (Katalog + ggf. `reasoning_effort`, CA-582); leer wenn der Job keine
   * Parameter unterstützt oder das Modell nicht im Katalog ist.
   */
  const [paramDefinitions, setParamDefinitions] = useState<LLMParameterDefinition[]>([]);
  const [isLoadingParams, setIsLoadingParams] = useState(false);

  useEffect(() => {
    loadConfigs();
    loadProvidersAndModels();
  }, []);

  const loadConfigs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const results = await Promise.all(JOB_DEFINITIONS.map((def) => def.load()));
      const nextConfigs: Record<string, LlmChoice | null> = {};
      JOB_DEFINITIONS.forEach((def, index) => {
        nextConfigs[def.id] = results[index];
      });
      setConfigs(nextConfigs);
    } catch (err: any) {
      console.error('Failed to load background job LLM configs:', err);
      setError('Background-Job-LLM-Einstellungen konnten nicht geladen werden');
      toast.error('Einstellungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProvidersAndModels = async () => {
    try {
      const [providersData, modelsData] = await Promise.all([
        // includeInactive: ALLE existierenden Provider anbieten (inaktive mit Badge),
        // damit z.B. azure-openai-eu vorkonfiguriert werden kann.
        apiService.getLlmProviders(true),
        apiService.getLlmModels({ includeInactive: false }),
      ]);
      setProviders(providersData.filter(p => p.name));
      setModels(modelsData.filter(m => m.status === 'active' && m.modelKey));
    } catch (err: any) {
      console.error('Failed to load providers and models:', err);
      toast.error('Provider und Modelle konnten nicht geladen werden');
    }
  };

  const editingJob = editingJobId
    ? JOB_DEFINITIONS.find((def) => def.id === editingJobId) ?? null
    : null;

  /**
   * Lädt die Parameter-Definitionen des gewählten Katalog-Modells — angeboten
   * wird ausschließlich der in der DB definierte Parameter-Katalog (CA-625,
   * keine Code-seitigen Zusatz-Parameter). Bei Jobs ohne Parameter-Support
   * oder unbekanntem Modell bleibt die Liste leer.
   */
  const loadParameterDefinitions = async (
    def: BackgroundJobLlmDefinition,
    providerName: string,
    modelKey: string,
  ) => {
    if (!def.supportsParameters || !providerName || !modelKey) {
      setParamDefinitions([]);
      return;
    }
    const provider = providers.find((p) => (p.name || p.id) === providerName);
    const model = models.find((m) => m.providerId === provider?.id && m.modelKey === modelKey);
    if (!model) {
      setParamDefinitions([]);
      return;
    }
    try {
      setIsLoadingParams(true);
      const resolved = await apiService.getModelParameters(model.id);
      setParamDefinitions(Object.values(resolved.parameters));
    } catch (err) {
      console.error('Failed to load model parameters:', err);
      toast.error('Modell-Parameter konnten nicht geladen werden');
      setParamDefinitions([]);
    } finally {
      setIsLoadingParams(false);
    }
  };

  /** Setzt bzw. entfernt (bei `undefined`) einen Parameter im Formular-Zustand. */
  const setParamValue = (name: string, value: LLMParameterValue | undefined) => {
    setFormData((prev) => {
      const llmParameters = { ...prev.llmParameters };
      if (value === undefined) {
        delete llmParameters[name];
      } else {
        llmParameters[name] = value;
      }
      return { ...prev, llmParameters };
    });
  };

  const handleSave = async () => {
    if (!editingJob) return;
    try {
      setIsSaving(true);
      // Jobs ohne Parameter-Unterstützung (Diktat-Transkription) dürfen kein
      // llmParameters-Feld senden: deren Backend-DTO kennt es nicht und die
      // globale ValidationPipe (forbidNonWhitelisted) lehnt es sonst mit 400 ab.
      const payload = editingJob.supportsParameters
        ? formData
        : { provider: formData.provider, model: formData.model };
      await editingJob.save(payload);
      toast.success(editingJob.savedMessage);
      await loadConfigs();
      setEditingJobId(null);
    } catch (err: any) {
      console.error('Failed to save background job LLM config:', err);
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const openEditDialog = (def: BackgroundJobLlmDefinition) => {
    const config = configs[def.id];
    // Unkonfigurierte Jobs starten mit leerem Formular (kein Default).
    setFormData({
      provider: config?.provider ?? '',
      model: config?.model ?? '',
      llmParameters: config?.llmParameters ?? {},
    });
    setEditingJobId(def.id);
    void loadParameterDefinitions(def, config?.provider ?? '', config?.model ?? '');
  };

  // Get models for a specific provider from the loaded data
  const getProviderModels = useMemo(() => {
    return (providerName: string) => {
      const provider = providers.find(p => p.name === providerName);
      if (!provider) return [];

      return models
        .filter(m => m.providerId === provider.id)
        .sort((a, b) => (a.displayName || a.modelKey).localeCompare(b.displayName || b.modelKey))
        .map(m => ({
          value: m.modelKey,
          label: m.displayName || m.modelKey,
        }));
    };
  }, [providers, models]);

  // Get provider display name from loaded data
  const getProviderLabel = (providerName: string) => {
    const provider = providers.find(p => p.name === providerName);
    return provider?.displayName || providerName;
  };

  /** Title-cased Anzeige-Label eines Parameter-Keys (`max_tokens` → `Max Tokens`). */
  const humanizeParamName = (name: string) =>
    name
      .split('_')
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(' ');

  /**
   * Rendert das Eingabefeld eines Modell-Parameters (CA-582). Nicht gesetzte
   * Felder bedeuten „Standard verwenden" (Key fehlt im gespeicherten Satz).
   * `array`-Parameter (z.B. stop-Sequenzen) werden bewusst nicht angeboten —
   * für Background-Jobs ohne Anwendungsfall.
   */
  const renderParameterField = (def: LLMParameterDefinition) => {
    if (def.type === 'array') return null;
    const isSet = def.name in formData.llmParameters;
    const value = formData.llmParameters[def.name];
    const defaultLabel = def.default !== undefined && def.default !== null ? String(def.default) : '';

    let field: ReactNode;
    if (def.type === 'enum') {
      field = (
        <Select
          value={isSet ? String(value) : '__default__'}
          onValueChange={(selected) => {
            if (selected === '__default__') {
              setParamValue(def.name, undefined);
              return;
            }
            // Enum-Werte typtreu zurückgeben (allowedValues kann Zahlen enthalten).
            const match = (def.allowedValues ?? []).find((allowed) => String(allowed) === selected);
            setParamValue(def.name, match ?? selected);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">Standard</SelectItem>
            {(def.allowedValues ?? []).map((allowed) => (
              <SelectItem key={String(allowed)} value={String(allowed)}>
                {String(allowed)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    } else if (def.type === 'bool') {
      field = (
        <Select
          value={isSet ? String(value) : '__default__'}
          onValueChange={(selected) =>
            setParamValue(def.name, selected === '__default__' ? undefined : selected === 'true')
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">Standard</SelectItem>
            <SelectItem value="true">An</SelectItem>
            <SelectItem value="false">Aus</SelectItem>
          </SelectContent>
        </Select>
      );
    } else if (def.type === 'int' || def.type === 'float') {
      field = (
        <Input
          type="number"
          value={isSet ? String(value) : ''}
          min={def.min ?? undefined}
          max={def.max ?? undefined}
          step={def.type === 'int' ? 1 : 0.1}
          placeholder={defaultLabel ? `Standard (${defaultLabel})` : 'Standard'}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              setParamValue(def.name, undefined);
              return;
            }
            const parsed = def.type === 'int' ? parseInt(raw, 10) : parseFloat(raw);
            if (!Number.isNaN(parsed)) {
              setParamValue(def.name, parsed);
            }
          }}
        />
      );
    } else {
      field = (
        <Input
          value={isSet ? String(value) : ''}
          placeholder={defaultLabel ? `Standard (${defaultLabel})` : 'Standard'}
          onChange={(e) => setParamValue(def.name, e.target.value === '' ? undefined : e.target.value)}
        />
      );
    }

    return (
      <div key={def.name} className="space-y-1">
        <Label htmlFor={`param-${def.name}`} title={def.description}>
          {humanizeParamName(def.name)}
          {(def.type === 'int' || def.type === 'float') && def.min != null && def.max != null && (
            <span className="ml-1 text-xs font-normal text-muted-foreground">({def.min}–{def.max})</span>
          )}
        </Label>
        {field}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Lade Einstellungen...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Cpu className="w-6 h-6" />
          Background Job LLMs
        </h1>
        <p className="text-gray-600">
          LLM-Provider und Modell pro Hintergrund-Job-Funktion. Diese Einstellungen werden bei
          der serverseitigen Content-Generierung verwendet, nicht im Live-Chat. Es gibt keine
          fest verdrahteten Defaults: unkonfigurierte Funktionen schlagen mit einer klaren
          Fehlermeldung fehl, bis hier ein Provider gewählt wurde. Wählbar sind ausschließlich
          admin-verwaltete Provider aus „LLM Provider" (mit eigenem API-Key) — Built-in-Provider
          stehen nicht zur Auswahl.
        </p>
      </div>

      {error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadConfigs}>Erneut versuchen</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Funktion</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Beschreibung</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Provider</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Modell</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {JOB_DEFINITIONS.map((def) => {
                    const config = configs[def.id];
                    const RowIcon = def.icon;
                    return (
                      <tr key={def.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 font-medium text-gray-900">
                            <RowIcon className={`w-4 h-4 ${def.iconClassName}`} />
                            {def.label}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 max-w-md">
                          {def.description}
                        </td>
                        <td className="py-3 px-4">
                          {config?.provider ? (
                            <Badge variant="outline" className="border-gray-300 bg-gray-50 text-gray-700">
                              {getProviderLabel(config.provider)}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Nicht konfiguriert
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-sm text-gray-900">
                          {config?.model || <span className="text-gray-400">&ndash;</span>}
                          {config?.llmParameters && Object.keys(config.llmParameters).length > 0 && (
                            <div className="mt-1 text-xs font-mono text-gray-500">
                              {Object.entries(config.llmParameters)
                                .map(([name, value]) => `${name}=${String(value)}`)
                                .join(' · ')}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(def)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editingJobId !== null} onOpenChange={(open) => { if (!open) setEditingJobId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJob?.dialogTitle}</DialogTitle>
            <DialogDescription>
              {editingJob?.dialogDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Provider Selection */}
            <div className="space-y-2">
              <Label htmlFor="provider">LLM Provider</Label>
              <Select
                value={formData.provider}
                onValueChange={(value) => {
                  if (editingJob?.modelFreetext) {
                    // Freitext-Modell (Deployment-Namen unterscheiden sich pro
                    // Provider) — beim Provider-Wechsel leeren.
                    setFormData({ ...formData, provider: value, model: '', llmParameters: {} });
                    return;
                  }
                  // Katalog-Modus: erstes Modell des Providers vorbelegen.
                  // Parameter sind modellspezifisch — beim Wechsel verwerfen.
                  const providerModels = getProviderModels(value);
                  const model = providerModels.length > 0 ? providerModels[0].value : '';
                  setFormData({ ...formData, provider: value, model, llmParameters: {} });
                  if (editingJob) {
                    void loadParameterDefinitions(editingJob, value, model);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Provider auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const selectable = editingJob?.providerFilter
                      ? providers.filter(editingJob.providerFilter)
                      : providers;
                    if (selectable.length === 0) {
                      return <SelectItem value="_none" disabled>Keine admin-verwalteten Provider vorhanden — zuerst unter „LLM Provider" anlegen</SelectItem>;
                    }
                    return selectable.map((provider) => (
                      <SelectItem key={provider.id} value={provider.name || provider.id}>
                        {provider.displayName || provider.name}
                        {provider.status !== 'active' ? ' (inaktiv)' : ''}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
              {(() => {
                const selected = providers.find(p => (p.name || p.id) === formData.provider);
                return selected && selected.status !== 'active' ? (
                  <p className="text-xs text-amber-600">
                    Dieser Provider ist inaktiv — er muss unter „LLM Provider" aktiviert werden, bevor der Job ihn nutzen kann.
                  </p>
                ) : null;
              })()}
            </div>

            {/* Model — Transkription: Freitext (Whisper-/Azure-Deployment-Namen
                weichen von den katalogisierten Modell-Keys ab; Modell-Liste liefert
                nur Autocomplete). Übrige Jobs: Katalog-Dropdown aus llm_models. */}
            <div className="space-y-2">
              <Label htmlFor="model">Modell</Label>
              {editingJob?.modelFreetext ? (
                <>
                  <Input
                    id="model"
                    list="background-job-llm-model-suggestions"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Modell- bzw. Deployment-Name eingeben"
                    disabled={!formData.provider}
                  />
                  <datalist id="background-job-llm-model-suggestions">
                    {getProviderModels(formData.provider || '').map((m: { value: string; label: string }) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </datalist>
                  {editingJob.modelHint && (
                    <p className="text-xs text-muted-foreground">{editingJob.modelHint}</p>
                  )}
                </>
              ) : (
                <Select
                  value={formData.model}
                  onValueChange={(value) => {
                    // Parameter sind modellspezifisch — beim Wechsel verwerfen.
                    setFormData({ ...formData, model: value, llmParameters: {} });
                    if (editingJob) {
                      void loadParameterDefinitions(editingJob, formData.provider, value);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Modell auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const providerModels = getProviderModels(formData.provider || '');
                      if (providerModels.length === 0) {
                        return <SelectItem value="_none" disabled>Keine Modelle verfügbar</SelectItem>;
                      }
                      return providerModels.map((m: { value: string; label: string }) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Modell-Parameter (CA-582) — nur bei Jobs mit Parameter-Support und gewähltem Modell */}
            {editingJob?.supportsParameters && formData.model && (
              <div className="space-y-3">
                <div>
                  <Label>Modell-Parameter</Label>
                  <p className="text-xs text-muted-foreground">
                    Nur gesetzte Felder werden gespeichert und überschreiben die Standardwerte der
                    Funktion; „Standard" bzw. leere Felder nutzen den bisherigen Wert aus dem Code.
                  </p>
                </div>
                {isLoadingParams ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Lade Parameter…
                  </div>
                ) : paramDefinitions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Für dieses Modell sind keine konfigurierbaren Parameter hinterlegt.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {paramDefinitions.map(renderParameterField)}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditingJobId(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !formData.model.trim() || !formData.provider}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
